import Box from '@mui/material/Box';
import React, { useEffect } from 'react';

import { CourierData } from '../../../../../@types/shipment.type';
import { SectionLabel } from '../../../../../components/atoms/SectionLabel';
import { DropdownItem } from '../../../../../components/atoms/SelectMenu';
import { QuickToggleGroup } from '../../../../../components/molecules/QuickToggleGroup';

export interface FreightCarrierSectionProps {
  /** The shipping-method toggle, so all three choices sit on one row. */
  leadingSlot?: React.ReactNode;
  /** The panel's actions, pinned to the top right of the same row. */
  trailingSlot?: React.ReactNode;
  couriers: CourierData[];
  selectedCourierId?: number;
  onSelectCourier: (courierId: number) => void;
  serviceTypes: DropdownItem[];
  serviceType?: string;
  onSelectServiceType: (value: string) => void;
}

/** The service level most shipments go out on, so it shouldn't need choosing. */
const PREFERRED_SERVICE_TYPE = /fixed\s*price\s*premium/i;

/** Who is carrying the freight, and at what service level. */
export const FreightCarrierSection: React.FC<FreightCarrierSectionProps> = ({
  leadingSlot,
  trailingSlot,
  couriers,
  selectedCourierId,
  onSelectCourier,
  serviceTypes,
  serviceType,
  onSelectServiceType,
}) => {
  // Pick Fixed Price Premium as soon as the courier's service types arrive, so
  // the common case needs no interaction. Only ever fills an empty choice — a
  // service type the user picked is never overwritten.
  //
  // Guarded on the list actually holding the current courier's options: on a
  // courier switch the caller clears `serviceType` while `serviceTypes` is
  // still the previous courier's list, and without the guard this effect would
  // select a service the new courier does not offer.
  useEffect(() => {
    if (serviceTypes.length === 0) return;
    if (serviceType) {
      // A choice that the current list no longer offers belongs to the courier
      // we just switched away from — drop it rather than submit it.
      if (!serviceTypes.some((item) => String(item.value) === String(serviceType))) {
        onSelectServiceType('');
      }
      return;
    }
    const preferred = serviceTypes.find((item) => PREFERRED_SERVICE_TYPE.test(item.label));
    if (preferred) onSelectServiceType(String(preferred.value));
    // `onSelectServiceType` is a fresh closure each render, so it is
    // deliberately not a dependency — including it would re-run this effect
    // continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceTypes, serviceType]);

  return (
    // The panel's actions sit at the top right of this row rather than at its
    // foot, where toasts were landing on top of them.
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2, rowGap: 2 }}>
      {/* Method, carrier and service type read left to right as one sequence of
          choices. Hairlines between them keep the rhythm even — the groups are
          very different widths, so gap alone left the spacing looking arbitrary. */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'stretch',
          gap: 3,
          rowGap: 2,
          '& > * + *': {
            pl: 3,
            borderLeft: 1,
            borderColor: 'v4.border',
          },
        }}
      >
        {leadingSlot}
        <Box>
          <SectionLabel>Carrier</SectionLabel>
          {couriers.length > 0 ? (
            <QuickToggleGroup
              label="Courier"
              options={couriers.map((courier) => ({
                value: String(courier.id),
                label: courier.name,
              }))}
              value={String(selectedCourierId ?? '')}
              onChange={(value) => onSelectCourier(Number(value))}
            />
          ) : (
            <Box sx={{ fontSize: '13px', color: 'text.secondary' }}>
              No freight-enabled couriers configured for this location.
            </Box>
          )}
        </Box>

        <Box>
          <SectionLabel>Service type</SectionLabel>
          {serviceTypes.length > 0 ? (
            // A toggle rather than a dropdown: there are only a handful, and
            // seeing the alternatives beats opening a menu to find them.
            <QuickToggleGroup
              label="Service type"
              options={serviceTypes.map((item) => ({
                value: String(item.value),
                label: item.label,
              }))}
              value={String(serviceType ?? '')}
              onChange={onSelectServiceType}
            />
          ) : (
            <Box sx={{ fontSize: '13px', color: 'text.secondary' }}>
              Choose a courier to see its service types.
            </Box>
          )}
        </Box>
      </Box>

      {trailingSlot && <Box sx={{ ml: 'auto' }}>{trailingSlot}</Box>}
    </Box>
  );
};
